import { supabase } from '../services/supabase'

export class WordPressService {
  constructor(siteUrl, username, password) {
    this.siteUrl = siteUrl.replace(/\/$/, '') // Remove trailing slash
    this.username = username
    this.password = password
    this.apiBase = `${this.siteUrl}/wp-json/wp/v2`
    // Create the Authorization header using application password
    this.auth = btoa(`${username}:${password}`)
    this.baseUrl = `${this.siteUrl}/wp-json/wp/v2`
  }

  async testConnection() {
    try {
      const response = await fetch(`${this.apiBase}/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${this.auth}`,
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        const error = await response.json()
        console.error('WordPress API error:', error)
        return false
      }
      
      const userData = await response.json()
      console.log('Successfully connected as:', userData.name)
      return true
    } catch (error) {
      console.error('WordPress connection test failed:', error)
      return false
    }
  }

  async uploadMedia(imageUrl) {
    try {
      // First fetch the image data
      const imageResponse = await fetch(imageUrl)
      const imageBlob = await imageResponse.blob()

      // Create form data
      const formData = new FormData()
      formData.append('file', imageBlob, 'featured-image.jpg')

      // Upload to WordPress
      const response = await fetch(`${this.baseUrl}/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${this.auth}`
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Failed to upload media: ${response.statusText}`)
      }

      const mediaData = await response.json()
      return mediaData.id // Return the media ID
    } catch (error) {
      console.error('Error uploading media:', error)
      throw error
    }
  }

  async createPost(articleData) {
    try {
      let featured_media = null;

      // If there's a featured image, upload it first
      if (articleData.featured_image?.url) {
        try {
          featured_media = await this.uploadMedia(articleData.featured_image.url);
          console.log('Successfully uploaded media:', featured_media);
        } catch (imageError) {
          console.error('Failed to upload featured image:', imageError);
        }
      }

      // Format content: handle multiple patterns of bold text
      let formattedContent = articleData.content || '';
      
      // Only format content if it exists
      if (formattedContent) {
        // First handle the complete patterns (### text ### or #### text ####)
        formattedContent = formattedContent.replace(/#{3,4}\s*(.*?)\s*#{3,4}/g, '<strong>$1</strong>');

        // Then handle line-starting patterns without closing hashes
        formattedContent = formattedContent.replace(/^#{3,4}\s*([^#\n]+)(?:\n|$)/gm, '<strong>$1</strong>\n');

        // Handle mid-line patterns without closing hashes
        formattedContent = formattedContent.replace(/\s#{3,4}\s*([^#\n]+)(?:\n|$)/gm, ' <strong>$1</strong>\n');

        // Handle tool names with hashes
        formattedContent = formattedContent.replace(/#{3,4}([A-Za-z0-9_-]+)#{3,4}/g, '<strong>$1</strong>');
        
        // Handle any remaining standalone hash patterns
        formattedContent = formattedContent.replace(/#{3,4}([^#\n\s][^#\n]*?)(?:\s|$)/g, '<strong>$1</strong> ');

        // Handle markdown-style bold
        formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Clean up any remaining hashes that might be legitimate content (like numbered lists)
        formattedContent = formattedContent.replace(/^(#{1,2}\s)/gm, '');
      }

      const postData = {
        title: articleData.title || '',
        content: formattedContent,
        status: 'draft',
        meta: {
          seed_keyword: articleData.seed_keyword || '',
          keyword: articleData.keyword || ''
        }
      };

      if (featured_media) {
        postData.featured_media = featured_media;
      }

      console.log('Creating post with data:', {
        title: postData.title,
        contentLength: formattedContent.length,
        hasFeaturedMedia: !!featured_media
      });

      const response = await fetch(`${this.baseUrl}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${this.auth}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to create post: ${JSON.stringify(errorData)}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating WordPress post:', error);
      throw error;
    }
  }

  async updatePost(postId, articleData) {
    try {
      let featured_media = null

      // If there's a featured image, upload it first
      if (articleData.featured_image?.url) {
        featured_media = await this.uploadMedia(articleData.featured_image.url)
      }

      const postData = {
        title: articleData.title,
        content: articleData.content,
        featured_media: featured_media
      }

      const response = await fetch(`${this.baseUrl}/posts/${postId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${this.auth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      })

      if (!response.ok) {
        throw new Error(`Failed to update post: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error updating WordPress post:', error)
      throw error
    }
  }
}

// Helper function to get WordPress settings for current user
export async function getWordPressSettings() {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return null
    }
    
    // First check if any settings exist
    const { data: settings, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()

    if (error && error.code !== 'PGRST116') {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching WordPress settings:', error)
      }
      return null
    }

    // If no settings exist, create an empty record
    if (!settings) {
      const { error: insertError } = await supabase
        .from('user_settings')
        .insert({
          user_id: user.id,
          wordpress_settings: null
        })
        .single()

      if (insertError && process.env.NODE_ENV === 'development') {
        console.error('Error creating initial settings:', insertError)
      }
      return null
    }

    return settings?.wordpress_settings || null
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error in getWordPressSettings:', error)
    }
    return null
  }
}

// Helper function to save WordPress settings
export async function saveWordPressSettings(settings) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { error } = await supabase
      .from('user_settings')
      .upsert({
        user_id: user.id,
        wordpress_settings: settings
      }, {
        onConflict: 'user_id'
      })

    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving WordPress settings:', error)
      }
      throw error
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error in saveWordPressSettings:', error)
    }
    throw error
  }
} 

